export type Candle = {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  trades: number;
};

export type IndicatorType = "SMA" | "EMA" | "RSI" | "MACD" | "BB";

export type IndicatorConfig = {
  type: IndicatorType;
  period: number;
  color: string;
  enabled: boolean;
  // MACD-specific
  fastPeriod?: number;
  slowPeriod?: number;
  signalPeriod?: number;
  // BB-specific
  stdDevMultiplier?: number;
};

export type LinePoint = { index: number; value: number };
export type MACDPoint = {
  index: number;
  macd: number;
  signal: number;
  histogram: number;
};
export type BBPoint = {
  index: number;
  upper: number;
  middle: number;
  lower: number;
};

export type IndicatorResult =
  | { type: "SMA"; color: string; points: LinePoint[] }
  | { type: "EMA"; color: string; points: LinePoint[] }
  | { type: "RSI"; color: string; points: LinePoint[] }
  | { type: "MACD"; color: string; points: MACDPoint[] }
  | { type: "BB"; color: string; points: BBPoint[] };

export const INDICATOR_DEFAULTS: Record<
  IndicatorType,
  { period: number; color: string }
> = {
  SMA: { period: 20, color: "#3b82f6" },
  EMA: { period: 12, color: "#f59e0b" },
  RSI: { period: 14, color: "#a855f7" },
  MACD: { period: 12, color: "#ef4444" },
  BB: { period: 20, color: "#6366f1" },
};

export function calculateSMA(
  candles: Candle[],
  period: number = 20,
): LinePoint[] {
  const points: LinePoint[] = [];
  if (candles.length < period) return points;

  let sum = 0;
  for (let i = 0; i < period; i++) sum += candles[i].close;
  points.push({ index: period - 1, value: sum / period });

  for (let i = period; i < candles.length; i++) {
    sum += candles[i].close - candles[i - period].close;
    points.push({ index: i, value: sum / period });
  }
  return points;
}

export function calculateEMA(
  candles: Candle[],
  period: number = 12,
): LinePoint[] {
  const points: LinePoint[] = [];
  if (candles.length < period) return points;

  const k = 2 / (period + 1);

  // Seed with SMA of first `period` candles
  let sum = 0;
  for (let i = 0; i < period; i++) sum += candles[i].close;
  let ema = sum / period;
  points.push({ index: period - 1, value: ema });

  for (let i = period; i < candles.length; i++) {
    ema = candles[i].close * k + ema * (1 - k);
    points.push({ index: i, value: ema });
  }
  return points;
}

function emaFromValues(values: number[], period: number): number[] {
  if (values.length < period) return [];
  const k = 2 / (period + 1);
  let sum = 0;
  for (let i = 0; i < period; i++) sum += values[i];
  let ema = sum / period;
  const result = new Array(period - 1).fill(NaN);
  result.push(ema);
  for (let i = period; i < values.length; i++) {
    ema = values[i] * k + ema * (1 - k);
    result.push(ema);
  }
  return result;
}

export function calculateRSI(
  candles: Candle[],
  period: number = 14,
): LinePoint[] {
  const points: LinePoint[] = [];
  if (candles.length < period + 1) return points;

  const changes: number[] = [];
  for (let i = 1; i < candles.length; i++) {
    changes.push(candles[i].close - candles[i - 1].close);
  }

  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 0; i < period; i++) {
    if (changes[i] > 0) avgGain += changes[i];
    else avgLoss += Math.abs(changes[i]);
  }
  avgGain /= period;
  avgLoss /= period;

  const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
  points.push({
    index: period,
    value: 100 - 100 / (1 + rs),
  });

  for (let i = period; i < changes.length; i++) {
    const gain = changes[i] > 0 ? changes[i] : 0;
    const loss = changes[i] < 0 ? Math.abs(changes[i]) : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    points.push({
      index: i + 1,
      value: 100 - 100 / (1 + rs),
    });
  }
  return points;
}

export function calculateMACD(
  candles: Candle[],
  fastPeriod: number = 12,
  slowPeriod: number = 26,
  signalPeriod: number = 9,
): MACDPoint[] {
  const closes = candles.map((c) => c.close);
  const fastEma = emaFromValues(closes, fastPeriod);
  const slowEma = emaFromValues(closes, slowPeriod);

  const macdLine: number[] = [];
  for (let i = 0; i < closes.length; i++) {
    if (isNaN(fastEma[i]) || isNaN(slowEma[i])) {
      macdLine.push(NaN);
    } else {
      macdLine.push(fastEma[i] - slowEma[i]);
    }
  }

  // Signal = EMA of MACD line (skip NaNs)
  const validMacd = macdLine.filter((v) => !isNaN(v));
  const signalEma = emaFromValues(validMacd, signalPeriod);

  const points: MACDPoint[] = [];
  let validIdx = 0;
  for (let i = 0; i < macdLine.length; i++) {
    if (isNaN(macdLine[i])) continue;
    if (validIdx < signalEma.length && !isNaN(signalEma[validIdx])) {
      points.push({
        index: i,
        macd: macdLine[i],
        signal: signalEma[validIdx],
        histogram: macdLine[i] - signalEma[validIdx],
      });
    }
    validIdx++;
  }
  return points;
}

export function calculateBB(
  candles: Candle[],
  period: number = 20,
  stdDevMultiplier: number = 2,
): BBPoint[] {
  const points: BBPoint[] = [];
  if (candles.length < period) return points;

  for (let i = period - 1; i < candles.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += candles[j].close;
    const mean = sum / period;

    let variance = 0;
    for (let j = i - period + 1; j <= i; j++) {
      variance += (candles[j].close - mean) ** 2;
    }
    const stdDev = Math.sqrt(variance / period);

    points.push({
      index: i,
      upper: mean + stdDevMultiplier * stdDev,
      middle: mean,
      lower: mean - stdDevMultiplier * stdDev,
    });
  }
  return points;
}

export function calculateIndicator(
  candles: Candle[],
  config: IndicatorConfig,
): IndicatorResult {
  switch (config.type) {
    case "SMA":
      return {
        type: "SMA",
        color: config.color,
        points: calculateSMA(candles, config.period),
      };
    case "EMA":
      return {
        type: "EMA",
        color: config.color,
        points: calculateEMA(candles, config.period),
      };
    case "RSI":
      return {
        type: "RSI",
        color: config.color,
        points: calculateRSI(candles, config.period),
      };
    case "MACD":
      return {
        type: "MACD",
        color: config.color,
        points: calculateMACD(
          candles,
          config.fastPeriod ?? 12,
          config.slowPeriod ?? 26,
          config.signalPeriod ?? 9,
        ),
      };
    case "BB":
      return {
        type: "BB",
        color: config.color,
        points: calculateBB(candles, config.period, config.stdDevMultiplier ?? 2),
      };
  }
}
